import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Icon } from '~/components';
import { Spinner } from '~/components/Spinner';
import { TrackHeader } from '~/modules/artist-roster/components/TrackDetails/TrackHeader';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

import { useArtistDetailsQuery } from '../../hooks/useArtistDetailsQuery';
import { useTrackDetailsQuery } from '../../hooks/useTrackDetailsQuery';
import { DataError } from '../DataError/DataError';
import { PerformanceView } from './PerformanceView';

export function TrackDetailsPage() {
  const { t } = useTranslation('roster');
  const { partyId } = useParams<{ partyId: string }>();
  const { familyId } = useParams<{ familyId: string }>();
  const { data: artistData } = useArtistDetailsQuery({ partyId });

  const {
    data: trackData,
    error: tracksError,
    isFetching: isFetchingTracks,
  } = useTrackDetailsQuery({ familyId });

  const artistName = artistData?.name;

  if (tracksError) {
    return (
      <DataError
        title={t('errors.loadingTrackDetails')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  if (!partyId || isNaN(Number(partyId))) {
    return (
      <div className="flex w-full items-center justify-start p-2xl">
        <p className="m-0 font-header text-display-xl leading-none text-default">
          {t('errors.invalidArtistId')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-between">
      <div className="flex w-full flex-col items-center justify-between gap-lg">
        <Breadcrumbs.Root className="flex w-full items-center justify-start gap-sm text-body-xs">
          <Breadcrumbs.Item
            to="/roster"
            className="font-semibold underline transition text-hover hover:text-white"
          >
            {t('header.heading')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item
            to={`/roster/${partyId}`}
            className="font-semibold underline transition text-hover hover:text-white"
          >
            {artistName}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>{trackData?.title || ''}</Breadcrumbs.Item>
        </Breadcrumbs.Root>
        {isFetchingTracks ? (
          <div className="flex w-full items-center justify-center p-2xl">
            <Spinner />
          </div>
        ) : trackData ? (
          <div className="flex w-full flex-col items-start gap-lg">
            <TrackHeader artistData={artistData} trackData={trackData} />
            <PerformanceView trackData={trackData} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

TrackDetailsPage.displayName = 'TrackDetailsPage';
